import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { irisError, classifyError } from '../errors';
import {
  ENTITY_TYPES,
  mergeEntityProfile,
  parseTags,
  tagsToText,
} from '../entityProfile';

const card = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 16,
  padding: 24,
};
const labelStyle = { fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px 0' };
const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid var(--border-strong)',
  background: 'var(--bg-input)',
  color: 'var(--text)',
  fontSize: 14,
  fontFamily: "'DM Sans', system-ui, sans-serif",
};
const btnPrimary = {
  padding: '10px 20px',
  borderRadius: 10,
  border: 'none',
  background: 'var(--accent)',
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: "'DM Sans', system-ui, sans-serif",
};
const btnSecondary = {
  padding: '8px 14px',
  borderRadius: 10,
  border: '1px solid var(--border-dashed)',
  background: 'var(--bg-input)',
  color: 'var(--text)',
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: "'DM Sans', system-ui, sans-serif",
};
const btnDanger = { ...btnSecondary, borderColor: 'color-mix(in srgb, var(--danger) 40%, transparent)', color: 'var(--danger)' };

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <p style={labelStyle}>{label}</p>
      {hint && <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: '0 0 8px 0' }}>{hint}</p>}
      {children}
    </div>
  );
}

/**
 * @param {string} clientId
 * @param {(row: object) => void} [onSaved] — called with { entity_profile } after save
 * @param {boolean} [compact] — tighter spacing for settings embed
 */
export default function EntityProfileForm({ clientId, onSaved, compact }) {
  const [profile, setProfile] = useState(() => mergeEntityProfile(null));
  const [serviceAreaText, setServiceAreaText] = useState('');
  const [expertiseText, setExpertiseText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    const { data, error: err } = await supabase
      .from('clients')
      .select('entity_profile')
      .eq('id', clientId)
      .single();
    setLoading(false);
    if (err) {
      setError(classifyError(err, 'IRIS-300'));
      return;
    }
    const merged = mergeEntityProfile(data?.entity_profile);
    setProfile(merged);
    setServiceAreaText(tagsToText(merged.service_area));
    setExpertiseText(tagsToText(merged.expertise));
    setError('');
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  function updateHq(key, val) {
    setProfile(p => ({ ...p, hq_address: { ...p.hq_address, [key]: val } }));
  }

  function setBranch(i, key, val) {
    setProfile(p => {
      const rows = [...p.branch_locations];
      rows[i] = { ...rows[i], [key]: val };
      return { ...p, branch_locations: rows };
    });
  }

  function addBranch() {
    setProfile(p => ({
      ...p,
      branch_locations: [...p.branch_locations, { label: '', street: '', city: '', state: '', zip: '' }],
    }));
  }

  function removeBranch(i) {
    setProfile(p => ({
      ...p,
      branch_locations: p.branch_locations.filter((_, j) => j !== i),
    }));
  }

  function setAuthLink(i, val) {
    setProfile(p => {
      const links = [...p.authority_links];
      links[i] = val;
      return { ...p, authority_links: links };
    });
  }

  function addAuthLink() {
    setProfile(p => ({ ...p, authority_links: [...p.authority_links, ''] }));
  }

  function removeAuthLink(i) {
    setProfile(p => ({ ...p, authority_links: p.authority_links.filter((_, j) => j !== i) }));
  }

  function setPriorityPage(i, key, val) {
    setProfile(p => {
      const rows = [...p.priority_pages];
      rows[i] = { ...rows[i], [key]: val };
      return { ...p, priority_pages: rows };
    });
  }

  function addPriorityPage() {
    setProfile(p => ({
      ...p,
      priority_pages: [...p.priority_pages, { title: '', url: '' }],
    }));
  }

  function removePriorityPage(i) {
    setProfile(p => ({
      ...p,
      priority_pages: p.priority_pages.filter((_, j) => j !== i),
    }));
  }

  function setCred(i, key, val) {
    setProfile(p => {
      const rows = [...p.credentials];
      rows[i] = { ...rows[i], [key]: val };
      return { ...p, credentials: rows };
    });
  }

  function addCred() {
    setProfile(p => ({
      ...p,
      credentials: [...p.credentials, { site: '', login: '', password: '' }],
    }));
  }

  function removeCred(i) {
    setProfile(p => ({
      ...p,
      credentials: p.credentials.filter((_, j) => j !== i),
    }));
  }

  const [visiblePwIdx, setVisiblePwIdx] = useState(null);
  const pwTimerRef = useRef(null);

  function revealPassword(i) {
    if (pwTimerRef.current) clearTimeout(pwTimerRef.current);
    setVisiblePwIdx(i);
    pwTimerRef.current = setTimeout(() => setVisiblePwIdx(null), 10000);
  }

  useEffect(() => {
    return () => { if (pwTimerRef.current) clearTimeout(pwTimerRef.current); };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);
    const payload = {
      ...profile,
      service_area: parseTags(serviceAreaText),
      expertise: parseTags(expertiseText),
      authority_links: profile.authority_links.map(s => s.trim()).filter(Boolean),
      branch_locations: profile.branch_locations.filter(
        b => b.street || b.city || b.label
      ),
      priority_pages: profile.priority_pages.filter(p => p.title?.trim() || p.url?.trim()),
      credentials: profile.credentials.filter(c => c.site?.trim() || c.login?.trim()),
    };
    const { error: upErr } = await supabase
      .from('clients')
      .update({ entity_profile: payload })
      .eq('id', clientId);
    setSaving(false);
    if (upErr) {
      setError(irisError('IRIS-301', upErr.message));
      return;
    }
    setProfile(payload);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
    if (onSaved) onSaved({ entity_profile: payload });
  }

  if (loading) {
    return (
      <div style={{ ...card, color: 'var(--text-dim)', fontSize: 14 }}>Loading business profile…</div>
    );
  }

  const mb = compact ? 12 : 16;

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: mb }}>
        <p style={{ fontSize: 13, color: 'var(--text-dim)', margin: '0 0 16px 0', lineHeight: 1.5 }}>
          Structured details for search & AI discovery (NAP, schema-style entity, llms.txt-style summary). Saved per company.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 16 }}>

      {/* Core identity */}
      <div style={card}>
        <p style={{ ...labelStyle, marginBottom: 16 }}>Core identity</p>
        <Field label="Legal business name">
          <input
            style={inputStyle}
            value={profile.legal_name}
            onChange={e => setProfile(p => ({ ...p, legal_name: e.target.value }))}
            placeholder='e.g. "Olympus Roofing"'
          />
        </Field>
        <Field label="Entity type">
          <select
            style={{ ...inputStyle, cursor: 'pointer' }}
            value={profile.entity_type}
            onChange={e => setProfile(p => ({ ...p, entity_type: e.target.value }))}
          >
            {ENTITY_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Founding date">
          <input
            type="date"
            style={{ ...inputStyle, colorScheme: 'inherit' }}
            value={profile.founding_date || ''}
            onChange={e => setProfile(p => ({ ...p, founding_date: e.target.value }))}
          />
        </Field>
        <Field label="Logo URL" hint="Paste a public image URL (HTTPS).">
          <input
            style={inputStyle}
            value={profile.logo_url}
            onChange={e => setProfile(p => ({ ...p, logo_url: e.target.value }))}
            placeholder="https://…"
          />
        </Field>
      </div>

      {/* NAP */}
      <div style={card}>
        <p style={{ ...labelStyle, marginBottom: 16 }}>Contact & location (NAP)</p>
        <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: '0 0 12px 0' }}>HQ address</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
          <input style={inputStyle} placeholder="Street" value={profile.hq_address.street}
            onChange={e => updateHq('street', e.target.value)} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <input style={inputStyle} placeholder="City" value={profile.hq_address.city}
              onChange={e => updateHq('city', e.target.value)} />
            <input style={inputStyle} placeholder="State" value={profile.hq_address.state}
              onChange={e => updateHq('state', e.target.value)} />
          </div>
          <input style={inputStyle} placeholder="ZIP" value={profile.hq_address.zip}
            onChange={e => updateHq('zip', e.target.value)} />
        </div>
        <Field label="Primary phone" hint='Use a consistent format, e.g. (801) 887-7663'>
          <input
            style={inputStyle}
            value={profile.primary_phone}
            onChange={e => setProfile(p => ({ ...p, primary_phone: e.target.value }))}
            placeholder="(801) 887-7663"
          />
        </Field>

        <p style={{ ...labelStyle, marginTop: 20 }}>Branch locations</p>
        {profile.branch_locations.map((b, i) => (
          <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Branch {i + 1}</span>
              <button type="button" style={btnDanger} onClick={() => removeBranch(i)}>Remove</button>
            </div>
            <input style={{ ...inputStyle, marginBottom: 8 }} placeholder="Label (e.g. Orem office)"
              value={b.label} onChange={e => setBranch(i, 'label', e.target.value)} />
            <input style={{ ...inputStyle, marginBottom: 8 }} placeholder="Street" value={b.street} onChange={e => setBranch(i, 'street', e.target.value)} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input style={inputStyle} placeholder="City" value={b.city} onChange={e => setBranch(i, 'city', e.target.value)} />
              <input style={inputStyle} placeholder="State" value={b.state} onChange={e => setBranch(i, 'state', e.target.value)} />
            </div>
            <input style={{ ...inputStyle, marginTop: 8 }} placeholder="ZIP" value={b.zip} onChange={e => setBranch(i, 'zip', e.target.value)} />
          </div>
        ))}
        <button type="button" style={btnSecondary} onClick={addBranch}>+ Add branch</button>
      </div>

      {/* Service & authority */}
      <div style={card}>
        <p style={{ ...labelStyle, marginBottom: 16 }}>Service & authority</p>
        <Field label="Service area" hint="Counties or regions — comma or newline separated.">
          <textarea
            style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
            value={serviceAreaText}
            onChange={e => setServiceAreaText(e.target.value)}
            placeholder="Salt Lake, Davis, Utah County…"
          />
        </Field>
        <Field label="Expertise (KnowsAbout)" hint="Specialties — comma or newline separated.">
          <textarea
            style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
            value={expertiseText}
            onChange={e => setExpertiseText(e.target.value)}
            placeholder="Asphalt Shingle, Metal Roofing, Flat Roofs…"
          />
        </Field>

        <p style={{ ...labelStyle, marginTop: 8 }}>Authority links (sameAs)</p>
        <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: '0 0 10px 0' }}>BBB, licensing, chamber, social profiles, etc.</p>
        {profile.authority_links.map((url, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input style={inputStyle} value={url} onChange={e => setAuthLink(i, e.target.value)} placeholder="https://…" />
            <button type="button" style={btnDanger} onClick={() => removeAuthLink(i)}>✕</button>
          </div>
        ))}
        <button type="button" style={btnSecondary} onClick={addAuthLink}>+ Add URL</button>
      </div>

      {/* AI discovery */}
      <div style={card}>
        <p style={{ ...labelStyle, marginBottom: 16 }}>AI discovery (llms.txt-style)</p>
        <Field label="AI summary" hint="2–3 sentences: elevator pitch for LLMs.">
          <textarea
            style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }}
            value={profile.ai_summary}
            onChange={e => setProfile(p => ({ ...p, ai_summary: e.target.value }))}
            placeholder="What you do, who you serve, and what makes you credible."
          />
        </Field>
        <p style={{ ...labelStyle, marginTop: 12 }}>Priority pages</p>
        <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: '0 0 10px 0' }}>Up to ~10 key pages for crawlers (title + URL).</p>
        {profile.priority_pages.map((pg, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <input style={inputStyle} placeholder="Page title" value={pg.title} onChange={e => setPriorityPage(i, 'title', e.target.value)} />
            <input style={inputStyle} placeholder="https://…" value={pg.url} onChange={e => setPriorityPage(i, 'url', e.target.value)} />
            <button type="button" style={btnDanger} onClick={() => removePriorityPage(i)}>✕</button>
          </div>
        ))}
        <button type="button" style={btnSecondary} onClick={addPriorityPage} disabled={profile.priority_pages.length >= 10}>
          + Add page {profile.priority_pages.length >= 10 ? '(max 10)' : ''}
        </button>
      </div>

      {/* Saved logins */}
      <div style={card}>
        <p style={{ ...labelStyle, marginBottom: 6 }}>Saved logins</p>
        <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: '0 0 14px 0' }}>
          Store credentials for client accounts (GBP, Yelp, hosting, etc.). Passwords are hidden by default.
        </p>
        {profile.credentials.map((c, i) => (
          <div key={i} style={{
            border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 10,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Login {i + 1}</span>
              <button type="button" style={btnDanger} onClick={() => removeCred(i)}>Remove</button>
            </div>
            <input style={{ ...inputStyle, marginBottom: 8 }} placeholder="Site (e.g. Google Business Profile)"
              value={c.site} onChange={e => setCred(i, 'site', e.target.value)} />
            <input style={{ ...inputStyle, marginBottom: 8 }} placeholder="Username or email"
              value={c.login} onChange={e => setCred(i, 'login', e.target.value)}
              autoComplete="off" />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                style={{ ...inputStyle, flex: 1, fontFamily: visiblePwIdx === i ? "'DM Sans', system-ui, sans-serif" : 'monospace' }}
                type={visiblePwIdx === i ? 'text' : 'password'}
                placeholder="Password"
                value={c.password}
                onChange={e => setCred(i, 'password', e.target.value)}
                autoComplete="new-password"
                data-lpignore="true"
              />
              <button type="button" onClick={() => revealPassword(i)}
                style={{
                  ...btnSecondary, fontSize: 12, padding: '8px 14px', flexShrink: 0,
                  color: visiblePwIdx === i ? 'var(--accent)' : 'var(--text)',
                }}>
                {visiblePwIdx === i ? 'Visible (10s)' : 'View'}
              </button>
            </div>
          </div>
        ))}
        <button type="button" style={btnSecondary} onClick={addCred}>+ Add login</button>
      </div>

      </div>{/* end grid */}

      {error && (
        <p style={{ color: 'var(--danger)', fontSize: 14, marginBottom: 12 }}>{error}</p>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button type="submit" style={btnPrimary} disabled={saving}>
          {saving ? 'Saving…' : 'Save business profile'}
        </button>
        {savedFlash && <span style={{ fontSize: 14, color: 'var(--success)', fontWeight: 600 }}>Saved</span>}
      </div>
    </form>
  );
}
